import { PAPER, lerpColor } from '../../color';
import { calmAt, calmCenter, catenary, glowDot, grain, heartPath, rgba, spots, starPath, vignette, type Painter } from '../../art/paint';
import { hsl, type World as Look } from '../../world';

const TAU = Math.PI * 2;
const FAR = hsl(280, 34, 16);
const LENS = hsl(280, 72, 82);
const GOLD = hsl(50, 88, 78);
const MINT = hsl(170, 85, 76);
const TINTS = [GOLD, MINT, LENS];
const GLYPHS = ['pandeiro', 'moon', 'star', 'lily', 'mushroom', 'pearl', 'dish', 'crystal', 'gear', 'lantern', 'record', 'sun', 'heart', 'drop'] as const;
const LEAVES = 6;

type Glyph = (typeof GLYPHS)[number];

interface Stage {
  readonly land: boolean;
  readonly beamY: number;
  readonly beamH: number;
  readonly floorY: number;
  readonly focal: { readonly x: number; readonly y: number; readonly r: number };
  readonly medal: number;
  readonly inset: number;
}

interface Rig {
  readonly x: number;
  readonly y: number;
  readonly can: boolean;
  readonly glyph: Glyph;
  readonly tint: number;
}

function stageOf(p: Painter): Stage {
  if (p.w / p.h >= 0.8) {
    const beamH = Math.max(8, p.unit * 0.04);
    return {
      land: true,
      beamY: p.h * 0.205,
      beamH,
      floorY: p.h * 0.83,
      focal: { x: Math.max(p.w * 0.1, Math.min(p.w * 0.16, p.cx - p.unit * 1.18)), y: p.h * 0.42, r: Math.max(24, Math.min(64, p.unit * 0.105)) },
      medal: Math.max(7, p.unit * 0.036),
      inset: Math.max(p.w * 0.03, beamH),
    };
  }
  return {
    land: false,
    beamY: p.h * 0.2,
    beamH: Math.max(6, p.unit * 0.035),
    floorY: Math.min(p.h * 0.8, p.cy + p.unit * 0.9),
    focal: { x: p.w * 0.2, y: p.h * 0.262, r: Math.max(18, p.unit * 0.13) },
    medal: Math.max(6, p.unit * 0.045),
    inset: 0,
  };
}

function rigOf(p: Painter, s: Stage): Rig[] {
  const out: Rig[] = [];
  let k = 0;
  const add = (x: number, y: number, can: boolean): void => {
    out.push({ x, y, can, glyph: GLYPHS[k % GLYPHS.length] ?? 'star', tint: TINTS[k % TINTS.length] ?? GOLD });
    if (!can) k += 1;
  };
  const clear = (x: number, y: number, gap: number): boolean =>
    !calmAt(p, x, y, 0.7) && Math.hypot(x - s.focal.x, y - s.focal.y) > s.focal.r + gap && Math.abs(x - s.focal.x) > s.focal.r * 1.2;
  const hang = s.beamY + s.beamH + s.medal * 1.5;
  if (s.land) {
    const slots = 14;
    const x0 = p.w * 0.07;
    const x1 = p.w * 0.93;
    for (let i = 0; i < slots; i++) {
      const x = x0 + ((x1 - x0) * (i + 0.5)) / slots;
      if (clear(x, hang, s.medal * 2)) add(x, hang, i % 3 === 1);
    }
    for (const x of [s.inset, p.w - s.inset]) {
      for (let j = 0; j < 4; j++) add(x, p.h * (0.36 + 0.12 * j), false);
    }
    return out;
  }
  for (const x of [p.w * 0.06, p.w * 0.36]) if (Math.abs(x - s.focal.x) > s.focal.r * 1.3) add(x, s.beamY + s.beamH * 1.2, true);
  const row = s.medal * 2.7;
  for (let r = 0; r < 2; r++) {
    for (let i = 0; i < 6; i++) {
      const x = p.w * (0.47 + 0.09 * i + (r === 1 ? 0.045 : 0));
      const y = hang + r * row;
      if (x < p.w - s.medal * 1.4 && clear(x, y, s.medal * 2)) add(x, y, false);
    }
  }
  return out;
}

function night(p: Painter, look: Look, s: Stage): void {
  const { ctx } = p;
  const bg = look.palette.bg;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(bg, 0x000000, 0.35)));
  g.addColorStop(0.5, rgba(bg));
  g.addColorStop(1, rgba(lerpColor(bg, LENS, 0.07)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  for (let i = 0; i < 44; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * s.beamY;
    ctx.fillStyle = rgba(look.palette.rim, p.rng.range(0.15, 0.45));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.6, 1.4), 0, TAU);
    ctx.fill();
  }
  glowDot(ctx, p.cx, s.floorY, p.unit * 1.3, LENS, 0.07);
}

function cone(p: Painter, x: number, y: number, tx: number, ty: number, half: number, tint: number, alpha: number): void {
  const { ctx } = p;
  const len = Math.hypot(tx - x, ty - y) || 1;
  const nx = (-(ty - y) / len) * half;
  const ny = ((tx - x) / len) * half;
  const g = ctx.createLinearGradient(x, y, tx, ty);
  g.addColorStop(0, rgba(tint, alpha));
  g.addColorStop(1, rgba(tint, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(x - nx * 0.08, y - ny * 0.08);
  ctx.lineTo(x + nx * 0.08, y + ny * 0.08);
  ctx.lineTo(tx + nx, ty + ny);
  ctx.lineTo(tx - nx, ty - ny);
  ctx.closePath();
  ctx.fill();
}

function truss(p: Painter, x0: number, y0: number, x1: number, y1: number, thick: number, color: number, alpha: number): void {
  const { ctx } = p;
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  const nx = (-(y1 - y0) / len) * (thick / 2);
  const ny = ((x1 - x0) / len) * (thick / 2);
  ctx.fillStyle = rgba(FAR, alpha * 0.6);
  ctx.beginPath();
  ctx.moveTo(x0 + nx, y0 + ny);
  ctx.lineTo(x1 + nx, y1 + ny);
  ctx.lineTo(x1 - nx, y1 - ny);
  ctx.lineTo(x0 - nx, y0 - ny);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(color, alpha);
  ctx.lineWidth = Math.max(1.2, thick * 0.14);
  ctx.beginPath();
  ctx.moveTo(x0 + nx, y0 + ny);
  ctx.lineTo(x1 + nx, y1 + ny);
  ctx.moveTo(x0 - nx, y0 - ny);
  ctx.lineTo(x1 - nx, y1 - ny);
  ctx.stroke();
  ctx.lineWidth = Math.max(0.8, thick * 0.07);
  ctx.beginPath();
  const steps = Math.max(2, Math.round(len / thick));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const side = i % 2 === 0 ? 1 : -1;
    const x = x0 + (x1 - x0) * t + nx * side;
    const y = y0 + (y1 - y0) * t + ny * side;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

function arch(p: Painter, look: Look, s: Stage): void {
  const color = lerpColor(look.ceil, look.palette.rim, 0.12);
  const mid = s.beamY + s.beamH / 2;
  if (s.land) {
    truss(p, s.inset, mid, s.inset, s.floorY, s.beamH * 0.9, color, 0.5);
    truss(p, p.w - s.inset, mid, p.w - s.inset, s.floorY, s.beamH * 0.9, color, 0.5);
  }
  truss(p, 0, mid, p.w, mid, s.beamH, color, 0.6);
}

function can(p: Painter, look: Look, x: number, y: number, r: number, tint: number): void {
  const { ctx } = p;
  glowDot(ctx, x, y + r * 0.5, r * 3.2, tint, 0.12);
  ctx.fillStyle = rgba(look.ceil, 0.95);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(look.palette.ink, 0.7);
  ctx.lineWidth = Math.max(1, r * 0.12);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(tint, PAPER, 0.3), 0.5);
  ctx.beginPath();
  ctx.arc(x, y + r * 0.15, r * 0.6, 0, TAU);
  ctx.fill();
}

function glyph(p: Painter, kind: Glyph, x: number, y: number, s: number): void {
  const { ctx } = p;
  ctx.beginPath();
  switch (kind) {
    case 'pandeiro':
      ctx.arc(x, y, s * 0.85, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * TAU - Math.PI / 2;
        ctx.moveTo(x + Math.cos(a) * s * 0.85 + s * 0.2, y + Math.sin(a) * s * 0.85);
        ctx.arc(x + Math.cos(a) * s * 0.85, y + Math.sin(a) * s * 0.85, s * 0.2, 0, TAU);
      }
      ctx.fill();
      return;
    case 'moon':
      ctx.arc(x, y, s * 0.8, 0, TAU);
      ctx.fill();
      ctx.fillStyle = rgba(FAR, 1);
      ctx.beginPath();
      ctx.arc(x + s * 0.38, y - s * 0.2, s * 0.68, 0, TAU);
      ctx.fill();
      return;
    case 'star':
      starPath(ctx, x, y, s, 0.45, 5, 0);
      ctx.fill();
      return;
    case 'lily':
      ctx.moveTo(x, y);
      ctx.arc(x, y, s * 0.85, -1.3, -1.3 + TAU - 0.6);
      ctx.closePath();
      ctx.fill();
      return;
    case 'mushroom':
      ctx.arc(x, y + s * 0.1, s * 0.85, Math.PI, TAU);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(x - s * 0.22, y + s * 0.1, s * 0.44, s * 0.7);
      return;
    case 'pearl':
      ctx.arc(x, y, s * 0.55, 0, TAU);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, s * 0.9, 0.2, Math.PI - 0.2);
      ctx.stroke();
      return;
    case 'dish':
      ctx.arc(x, y - s * 0.35, s * 0.85, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(x - s * 0.1, y + s * 0.4, s * 0.2, s * 0.45);
      return;
    case 'crystal':
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s * 0.6, y - s * 0.2);
      ctx.lineTo(x, y + s);
      ctx.lineTo(x - s * 0.6, y - s * 0.2);
      ctx.closePath();
      ctx.fill();
      return;
    case 'gear':
      starPath(ctx, x, y, s * 0.95, 0.72, 8, 0);
      ctx.fill();
      ctx.fillStyle = rgba(FAR, 1);
      ctx.beginPath();
      ctx.arc(x, y, s * 0.3, 0, TAU);
      ctx.fill();
      return;
    case 'lantern':
      ctx.fillRect(x - s * 0.42, y - s * 0.5, s * 0.84, s * 1.2);
      ctx.beginPath();
      ctx.arc(x, y - s * 0.5, s * 0.3, Math.PI, TAU);
      ctx.stroke();
      return;
    case 'record':
      ctx.arc(x, y, s * 0.9, 0, TAU);
      ctx.moveTo(x + s * 0.55, y);
      ctx.arc(x, y, s * 0.55, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, s * 0.2, 0, TAU);
      ctx.fill();
      return;
    case 'sun':
      ctx.arc(x, y, s * 0.42, 0, TAU);
      ctx.fill();
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * TAU;
        ctx.moveTo(x + Math.cos(a) * s * 0.62, y + Math.sin(a) * s * 0.62);
        ctx.lineTo(x + Math.cos(a) * s, y + Math.sin(a) * s);
      }
      ctx.stroke();
      return;
    case 'heart':
      heartPath(ctx, x, y + s * 0.35, s * 0.85);
      ctx.fill();
      return;
    case 'drop':
      ctx.moveTo(x, y - s);
      ctx.lineTo(x + s * 0.5, y + s * 0.1);
      ctx.arc(x, y + s * 0.3, s * 0.54, -0.37, Math.PI + 0.37);
      ctx.closePath();
      ctx.fill();
  }
}

function medallion(p: Painter, look: Look, rig: Rig, r: number): void {
  const { ctx } = p;
  ctx.fillStyle = rgba(FAR, 0.92);
  ctx.beginPath();
  ctx.arc(rig.x, rig.y, r, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(look.ceil, look.palette.rim, 0.25), 0.7);
  ctx.lineWidth = Math.max(1, r * 0.12);
  ctx.stroke();
  ctx.save();
  ctx.strokeStyle = rgba(rig.tint, 0.5);
  ctx.fillStyle = rgba(rig.tint, 0.42);
  ctx.lineWidth = Math.max(1, r * 0.1);
  glyph(p, rig.glyph, rig.x, rig.y, r * 0.6);
  ctx.restore();
}

function rigging(p: Painter, look: Look, s: Stage, rig: readonly Rig[]): void {
  const { ctx } = p;
  const canR = Math.max(5, p.unit * 0.032);
  for (const r of rig) {
    if (!r.can) continue;
    cone(p, r.x, r.y + canR, r.x + (r.x - p.cx) * 0.5, s.floorY, p.unit * 0.17, r.tint, 0.07);
  }
  arch(p, look, s);
  ctx.strokeStyle = rgba(look.palette.ink, 0.6);
  ctx.lineWidth = Math.max(1, s.medal * 0.14);
  for (const r of rig) {
    if (r.can || Math.abs(r.y - (s.beamY + s.beamH + s.medal * 1.5)) > 1) continue;
    ctx.beginPath();
    ctx.moveTo(r.x, s.beamY + s.beamH);
    ctx.lineTo(r.x, r.y - s.medal);
    ctx.stroke();
  }
  for (const r of rig) {
    if (r.can) can(p, look, r.x, r.y, canR, r.tint);
    else medallion(p, look, r, s.medal);
  }
}

function trophy(p: Painter, look: Look, s: Stage): void {
  const { ctx } = p;
  const pal = look.palette;
  const { x, y, r } = s.focal;
  const top = s.beamY + s.beamH;
  ctx.strokeStyle = rgba(lerpColor(LENS, pal.bg, 0.3), 0.55);
  ctx.lineWidth = Math.max(2, r * 0.12);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x - r * 0.5, top);
  ctx.lineTo(x - r * 0.12, y - r * 0.92);
  ctx.moveTo(x + r * 0.5, top);
  ctx.lineTo(x + r * 0.12, y - r * 0.92);
  ctx.stroke();
  glowDot(ctx, x, y, r * 2.6, GOLD, 0.12);
  ctx.fillStyle = rgba(lerpColor(pal.heldShade, pal.bg, 0.45), 0.9);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(pal.heldShade, 0.7);
  ctx.lineWidth = Math.max(1.5, r * 0.08);
  ctx.stroke();
  ctx.strokeStyle = rgba(pal.heldShade, 0.45);
  ctx.lineWidth = Math.max(1, r * 0.03);
  ctx.beginPath();
  ctx.arc(x, y, r * 0.84, 0, TAU);
  ctx.stroke();
  ctx.fillStyle = rgba(pal.heldShade, 0.55);
  for (const side of [-1, 1]) {
    for (let i = 0; i < LEAVES; i++) {
      const a = Math.PI / 2 + side * (0.4 + (i / LEAVES) * 2.2);
      const lx = x + Math.cos(a) * r * 0.64;
      const ly = y + Math.sin(a) * r * 0.64;
      const tx = -Math.sin(a) * side + Math.cos(a) * 0.35;
      const ty = Math.cos(a) * side + Math.sin(a) * 0.35;
      const n = Math.hypot(tx, ty) || 1;
      const dx = tx / n;
      const dy = ty / n;
      const len = r * 0.14;
      const wid = len * 0.38;
      ctx.beginPath();
      ctx.moveTo(lx + dx * len, ly + dy * len);
      ctx.lineTo(lx - dy * wid, ly + dx * wid);
      ctx.lineTo(lx - dx * len * 0.6, ly - dy * len * 0.6);
      ctx.lineTo(lx + dy * wid, ly - dx * wid);
      ctx.closePath();
      ctx.fill();
    }
  }
  starPath(ctx, x, y, r * 0.36, 0.45, 5, 0);
  ctx.fillStyle = rgba(GOLD, 0.6);
  ctx.fill();
}

function floor(p: Painter, look: Look, s: Stage): void {
  const { ctx } = p;
  const g = ctx.createLinearGradient(0, s.floorY, 0, p.h);
  g.addColorStop(0, rgba(FAR, 0.75));
  g.addColorStop(1, rgba(lerpColor(FAR, 0x000000, 0.4), 0.9));
  ctx.fillStyle = g;
  ctx.fillRect(0, s.floorY, p.w, p.h - s.floorY);
  ctx.strokeStyle = rgba(lerpColor(look.palette.path, look.palette.bg, 0.45), 0.45);
  ctx.lineWidth = Math.max(1.5, p.unit * 0.006);
  ctx.beginPath();
  ctx.moveTo(0, s.floorY);
  ctx.lineTo(p.w, s.floorY);
  ctx.stroke();
  const step = p.unit * 0.16;
  for (let x = step / 2; x < p.w; x += step) {
    if (calmAt(p, x, s.floorY, 0.7)) continue;
    glowDot(ctx, x, s.floorY - 2, p.unit * 0.03, GOLD, 0.2);
    ctx.fillStyle = rgba(GOLD, 0.45);
    ctx.beginPath();
    ctx.arc(x, s.floorY - 2, Math.max(1.2, p.unit * 0.006), 0, TAU);
    ctx.fill();
  }
}

function monitors(p: Painter, look: Look, s: Stage): void {
  const { ctx } = p;
  const pal = look.palette;
  const mw = Math.max(26, p.unit * 0.17);
  const mh = mw * 0.5;
  const xs = s.land ? [p.w * 0.1, p.w * 0.9] : [p.w * 0.12, p.w * 0.88];
  for (const x of xs) {
    const y = s.floorY;
    const out = x < p.cx ? -1 : 1;
    ctx.strokeStyle = rgba(pal.ink, 0.7);
    ctx.lineWidth = Math.max(1.5, p.unit * 0.007);
    for (let c = 0; c < 2; c++) {
      const x1 = x + out * mw * (1.1 + c * 0.7);
      const y1 = p.h + 4;
      ctx.beginPath();
      for (let i = 0; i <= 12; i++) {
        const pt = catenary(x + out * mw * 0.3, y + mh * 0.15, x1, y1, -mh * (0.6 + 0.4 * c), i / 12);
        if (i === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      }
      ctx.stroke();
    }
    ctx.fillStyle = rgba(lerpColor(pal.ink, FAR, 0.3), 0.92);
    ctx.beginPath();
    ctx.moveTo(x - mw / 2, y + mh * 0.25);
    ctx.lineTo(x + mw / 2, y + mh * 0.25);
    ctx.lineTo(x + mw * 0.36, y - mh * 0.7);
    ctx.lineTo(x - mw * 0.36, y - mh * 0.7);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(look.ceil, 0.8);
    ctx.lineWidth = Math.max(1, p.unit * 0.004);
    ctx.stroke();
    ctx.fillStyle = rgba(MINT, 0.45);
    ctx.beginPath();
    ctx.arc(x + mw * 0.24, y - mh * 0.45, Math.max(1.2, mw * 0.03), 0, TAU);
    ctx.fill();
  }
}

function crowd(p: Painter, look: Look, s: Stage, lighters: boolean): void {
  const { ctx } = p;
  const head = Math.max(5, p.unit * 0.032);
  const base = s.land ? p.h * 0.925 : Math.max(s.floorY + head * 4, p.h * 0.86);
  const tone = rgba(lerpColor(look.palette.bg, look.palette.ink, 0.6), 0.92);
  const flames: { x: number; y: number; r: number }[] = [];
  ctx.fillStyle = tone;
  ctx.strokeStyle = tone;
  ctx.lineCap = 'round';
  for (let x = -head; x < p.w + head; ) {
    const y = base + p.rng.range(-0.5, 0.6) * head;
    const r = head * p.rng.range(0.85, 1.15);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x, y + r * 2.1, r * 1.9, r * 1.3, 0, Math.PI, TAU);
    ctx.fill();
    ctx.fillRect(x - r * 1.9, y + r * 2.08, r * 3.8, p.h - y);
    const raised = p.rng.chance(0.3);
    const side = p.rng.chance(0.5) ? 1 : -1;
    const reach = p.rng.range(2.2, 3.4);
    const lit = p.rng.chance(0.45);
    if (raised) {
      const hx = x + side * r * 1.6;
      const hy = y - r * reach;
      ctx.lineWidth = r * 0.55;
      ctx.beginPath();
      ctx.moveTo(x + side * r * 1.3, y + r * 1.6);
      ctx.lineTo(hx, hy);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(hx, hy, r * 0.42, 0, TAU);
      ctx.fill();
      if (lighters && lit) flames.push({ x: hx, y: hy - r * 0.95, r });
    }
    x += r * p.rng.range(2.6, 3.4);
  }
  for (const f of flames) {
    glowDot(ctx, f.x, f.y, f.r * 2.4, GOLD, 0.16);
    const s2 = f.r * 0.5;
    ctx.fillStyle = rgba(GOLD, 0.5);
    ctx.beginPath();
    ctx.moveTo(f.x, f.y - s2);
    ctx.lineTo(f.x + s2 * 0.5, f.y + s2 * 0.1);
    ctx.arc(f.x, f.y + s2 * 0.3, s2 * 0.54, -0.37, Math.PI + 0.37);
    ctx.closePath();
    ctx.fill();
  }
}

function confettiField(p: Painter, s: Stage): void {
  const { ctx } = p;
  const size = Math.max(3, p.unit * 0.012);
  for (const pt of spots(p, 30, 0.9, 8, p.unit * 0.09)) {
    if (pt.y < s.beamY + s.beamH * 2 || pt.y > s.floorY) continue;
    if (Math.hypot(pt.x - s.focal.x, pt.y - s.focal.y) < s.focal.r * 1.5) continue;
    ctx.save();
    ctx.translate(pt.x, pt.y);
    ctx.rotate(p.rng.float() * TAU);
    ctx.fillStyle = rgba(p.rng.pick(TINTS), 0.32);
    ctx.fillRect(-size, -size * 0.45, size * 2, size * 0.9);
    ctx.restore();
  }
}

export function paint(p: Painter, look: Look): void {
  const s = stageOf(p);
  night(p, look, s);
  rigging(p, look, s, rigOf(p, s));
  trophy(p, look, s);
  floor(p, look, s);
  if (p.level >= 2) monitors(p, look, s);
  crowd(p, look, s, p.level >= 3);
  if (p.level >= 3) confettiField(p, s);
  calmCenter(p, look.palette.bg, 0.65, 1.15);
  grain(p, 0.05, true);
  vignette(p, look.palette.ink, 0.45);
}
